import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
  token: string | null;
  username: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (usernameInput: string, passwordInput: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Start true to check localStorage
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for token in localStorage on initial load
    try {
      const storedToken = localStorage.getItem('authToken');
      const storedUsername = localStorage.getItem('authUsername');
      if (storedToken && storedUsername) {
        setToken(storedToken);
        setUsername(storedUsername);
      }
    } catch (e) {
      console.error('Failed to access localStorage:', e);
      // Handle environments where localStorage is not available or restricted
    }
    setIsLoading(false);
  }, []);

  const login = async (usernameInput: string, passwordInput: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: usernameInput, password: passwordInput }),
      });

      const data = await response.json();

      if (response.ok && data.token && data.username) {
        setToken(data.token);
        setUsername(data.username);
        try {
            localStorage.setItem('authToken', data.token);
            localStorage.setItem('authUsername', data.username);
        } catch (e) {
            console.error('Failed to save to localStorage:', e);
            setError('Login successful, but failed to save session. Please enable cookies/localStorage.');
            // Still allow login for the current session if localStorage fails
        }
      } else {
        setError(data.message || 'Login failed. Please check your credentials.');
        throw new Error(data.message || 'Login failed');
      }
    } catch (err: any) {
      console.error('Login request error:', err);
      setError(err.message || 'An unexpected error occurred during login.');
      throw err; // Re-throw to be caught by the LoginPage component
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setToken(null);
    setUsername(null);
    try {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUsername');
    } catch (e) {
        console.error('Failed to remove from localStorage:', e);
    }
    // Optionally, redirect to login page or make an API call to invalidate server-side session/cookie
  };

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider value={{ token, username, isAuthenticated, isLoading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
